import {
	Fragment,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import type { ILocationProps } from "../../services/Location/types";
import {
	Circle,
	CircleMarker,
	MapContainer,
	Marker,
	Popup,
	TileLayer,
	useMap,
	useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import DriftMarker from "react-leaflet-drift-marker";
import { IconButton, Tooltip as MuiTooltip } from "@mui/material";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import StopIcon from "@mui/icons-material/Stop";
import vanIcon from "../../assets/images/icons/map/vanIcon.webp";
const defaultIcon = L.icon({
	iconUrl: vanIcon,
	iconRetinaUrl: vanIcon,
	iconSize: [40, 41],
	iconAnchor: [12, 41],
	popupAnchor: [8, -34],
	shadowSize: [41, 41],
});
import { t } from "i18next";
import L from "leaflet";
import { useLocations } from "../../pages/LocationPage/_components/LocationContext";
import type {
	GeofenceCenter,
	Geofence,
} from "../../pages/LocationPage/_components/geofenceUtils";
import { isPointInsideGeofence } from "../../pages/LocationPage/_components/geofenceUtils";
import { AppContext } from "../../context/AppContextProvider";

interface MapViewProps {
	selectedId?: string | null;
	selectedIds: string[];
	onSelect: (id: string) => void;
	geofences?: Geofence[];
	geofencePanelOpen?: boolean;
	isPickingFenceCenter?: boolean;
	pickingGeofenceId?: string | null;
	focusedGeofenceId?: string | null;
	onFenceCenterSelected?: (id: string, center: GeofenceCenter) => void;
	onStopPickingFenceCenter?: () => void;
	onToggleGeofencePanel?: () => void;
	onStopFollow?: () => void;
}

type MappableLocation = ILocationProps & {
	latitude: number;
	longitude: number;
};

function escapeHtml(value: string): string {
	return value
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;")
		.replaceAll("'", "&#39;");
}

function createPlateIcon(plate: string): L.DivIcon {
	return L.divIcon({
		className: "vehicle-plate-marker",
		html: `
            <div class="vehicle-plate-marker__container">
                <span class="vehicle-plate-marker__text">${escapeHtml(plate)}</span>
            </div>
        `,
		iconSize: [90, 16],
		iconAnchor: [37, 2],
	});
}

function splitAddressAfterCity(address?: string) {
	const normalized = address?.trim();

	if (!normalized) {
		return {
			firstLine: "Endereço não encontrado",
			secondLine: null as string | null,
		};
	}

	const citySeparatorIndex = normalized.indexOf(" - ");
	if (citySeparatorIndex < 0) {
		return { firstLine: normalized, secondLine: null as string | null };
	}

	return {
		firstLine: normalized.slice(0, citySeparatorIndex),
		secondLine: normalized.slice(citySeparatorIndex + 3),
	};
}

function formatPlateDisplay(plate?: string) {
	const normalized = plate?.trim();

	if (!normalized) {
		return "-";
	}

	const upperText = normalized.toUpperCase();
	const plateMatch = upperText.match(/([A-Z]{3})[\s-]?([A-Z0-9]{4})/);

	if (plateMatch) {
		return `${plateMatch[1]}-${plateMatch[2]}`;
	}

	const alphanumeric = upperText.replace(/[^A-Z0-9]/g, "");

	if (!alphanumeric) {
		return normalized;
	}

	const base = alphanumeric.slice(0, 7);
	return base.length <= 3 ? base : `${base.slice(0, 3)}-${base.slice(3)}`;
}

function hasCoordinates(
	location: ILocationProps,
): location is MappableLocation {
	return (
		location.hasLocation === true &&
		typeof location.latitude === "number" &&
		Number.isFinite(location.latitude) &&
		typeof location.longitude === "number" &&
		Number.isFinite(location.longitude)
	);
}

function MapController({
	selectedLocation,
	isFollowing,
}: {
	selectedLocation?: MappableLocation;
	isFollowing: boolean;
}) {
	const map = useMap();

	useEffect(() => {
		if (isFollowing && selectedLocation) {
			map.flyTo([selectedLocation.latitude, selectedLocation.longitude], 17, {
				animate: true,
				duration: 1,
			});
		}
	}, [isFollowing, map, selectedLocation]);

	return null;
}

function FocusedGeofenceController({
	geofence,
}: {
	geofence: Geofence | null;
}) {
	const map = useMap();

	useEffect(() => {
		if (!geofence?.center) {
			return;
		}

		map.flyTo([geofence.center.latitude, geofence.center.longitude], 17, {
			animate: true,
			duration: 0.8,
		});
	}, [geofence, map]);

	return null;
}

function ResizeController({ trigger }: { trigger?: boolean }) {
	const map = useMap();

	useEffect(() => {
		const timer = setTimeout(() => {
			// touch trigger so hook dependency is necessary
			if (typeof trigger !== "undefined") {
				map.invalidateSize();
				map.setView(map.getCenter(), map.getZoom(), {
					animate: true,
					duration: 0.5,
				});
			}
		}, 1);

		return () => clearTimeout(timer);
	}, [map, trigger]);

	return null;
}

function MapClickController({
	isPickingFenceCenter,
	pickingGeofenceId,
	onFenceCenterSelected,
	onStopPickingFenceCenter,
}: {
	isPickingFenceCenter: boolean;
	pickingGeofenceId: string | null | undefined;
	onFenceCenterSelected: (id: string, center: GeofenceCenter) => void;
	onStopPickingFenceCenter: () => void;
}) {
	useMapEvents({
		click: (event) => {
			if (!isPickingFenceCenter || !pickingGeofenceId) {
				return;
			}

			onFenceCenterSelected(pickingGeofenceId, {
				latitude: event.latlng.lat,
				longitude: event.latlng.lng,
			});
			onStopPickingFenceCenter();
		},
	});

	return null;
}

function GeofenceOverlay({ geofence }: { geofence: Geofence }) {
	if (!geofence.enabled || !geofence.center) {
		return null;
	}

	return (
		<>
			<Circle
				center={[geofence.center.latitude, geofence.center.longitude]}
				radius={geofence.radiusMeters}
				pathOptions={{
					color: "#2563eb",
					weight: 2,
					fillColor: "#38bdf8",
					fillOpacity: 0.12,
				}}
			/>
			<CircleMarker
				center={[geofence.center.latitude, geofence.center.longitude]}
				radius={8}
				pathOptions={{
					color: "#0f172a",
					weight: 2,
					fillColor: "#f97316",
					fillOpacity: 1,
				}}
			/>
		</>
	);
}

function GeofenceNotificationController({
	geofence,
	locations,
}: {
	geofence: Geofence;
	locations: ILocationProps[];
}) {
	const { showSnackbar } = useContext(AppContext);
	const previousStatusRef = useRef<Record<string, boolean>>({});
	const previousFenceKeyRef = useRef<string | null>(null);

	const fenceKey = geofence.center
		? `${geofence.id}-${geofence.enabled}-${geofence.center.latitude}-${geofence.center.longitude}-${geofence.radiusMeters}`
		: `${geofence.id}-${geofence.enabled}-empty-${geofence.radiusMeters}`;

	useEffect(() => {
		if (previousFenceKeyRef.current !== fenceKey) {
			previousFenceKeyRef.current = fenceKey;
			previousStatusRef.current = {};
		}
	}, [fenceKey]);

	useEffect(() => {
		if (!geofence.enabled || !geofence.center) {
			return;
		}

		const canUseBrowserNotification =
			typeof window !== "undefined" && "Notification" in window;

		const maybeSendBrowserNotification = async (
			title: string,
			body: string,
		) => {
			if (!canUseBrowserNotification) {
				return;
			}

			if (window.Notification.permission === "default") {
				try {
					await window.Notification.requestPermission();
				} catch {
					return;
				}
			}

			if (window.Notification.permission !== "granted") {
				return;
			}

			new window.Notification(title, { body });
		};

		for (const location of locations) {
			if (
				typeof location.latitude !== "number" ||
				typeof location.longitude !== "number"
			) {
				continue;
			}

			const currentInside = isPointInsideGeofence(
				location.latitude,
				location.longitude,
				geofence,
			);
			const previousInside = previousStatusRef.current[location.entityId];

			if (typeof previousInside === "undefined") {
				previousStatusRef.current[location.entityId] = currentInside;
				continue;
			}

			if (previousInside === currentInside) {
				continue;
			}

			previousStatusRef.current[location.entityId] = currentInside;

			const plate = location.vehiclePlate?.trim() || location.entityId;
			const fenceName = geofence.name?.trim() || `Cerca ${geofence.id}`;
			const message = currentInside
				? `${t("map.vehicle")} ${plate} ${t("map.enteredFence")} "${fenceName}".`
				: `${t("map.vehicle")} ${plate} ${t("map.leftFence")} "${fenceName}".`;

			showSnackbar(message, currentInside ? "success" : "warning");
			void maybeSendBrowserNotification(
				`${t("map.virtualFence")} — ${fenceName}`,
				message,
			);
		}
	}, [geofence, locations, showSnackbar]);

	return null;
}

function InitialBoundsController({
	locations,
}: {
	locations: ILocationProps[];
}) {
	const map = useMap();
	const hasFitted = useRef(false);

	useEffect(() => {
		if (!hasFitted.current && locations.length > 0) {
			const bounds = L.latLngBounds(
				locations
					.filter(hasCoordinates)
					.map((loc) => [loc.latitude, loc.longitude]),
			);
			if (bounds.isValid()) {
				map.fitBounds(bounds, { padding: [50, 50] });
				hasFitted.current = true;
			}
		}
	}, [locations, map]);

	return null;
}

export default function MapView({
	selectedId,
	selectedIds,
	onSelect,
	geofences,
	geofencePanelOpen = false,
	isPickingFenceCenter = false,
	pickingGeofenceId,
	focusedGeofenceId,
	onFenceCenterSelected,
	onStopPickingFenceCenter,
	onToggleGeofencePanel,
	onStopFollow,
}: MapViewProps) {
	const locations = useLocations();
	const [isFollowing, setIsFollowing] = useState(true);
	const resolvedGeofences = geofences ?? [
		{ id: "default", enabled: false, center: null, radiusMeters: 300 },
	];

	useEffect(() => {
		if (selectedId) {
			setIsFollowing(true);
		}
	}, [selectedId]);

	const markersToRender = useMemo<MappableLocation[]>(() => {
		return locations
			.filter(
				(l) => selectedIds.length === 0 || selectedIds.includes(l.entityId),
			)
			.filter(hasCoordinates);
	}, [locations, selectedIds]);

	const selectedLocation = useMemo(() => {
		if (!selectedId) {
			return undefined;
		}

		const found = locations.find(
			(location) => location.entityId === selectedId,
		);
		return found && hasCoordinates(found) ? found : undefined;
	}, [locations, selectedId]);

	const focusedGeofence = useMemo(() => {
		if (!focusedGeofenceId) {
			return null;
		}

		return (
			resolvedGeofences.find((geofence) => geofence.id === focusedGeofenceId) ??
			null
		);
	}, [focusedGeofenceId, resolvedGeofences]);

	return (
		<MapContainer
			center={[0, 0]}
			zoom={2}
			className="map-container"
			style={{ height: "100%", width: "100%" }}
		>
			<TileLayer
				url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
			/>

			{resolvedGeofences.map((g) => (
				<GeofenceOverlay key={g.id} geofence={g} />
			))}

			<MapClickController
				isPickingFenceCenter={isPickingFenceCenter}
				pickingGeofenceId={pickingGeofenceId}
				onFenceCenterSelected={onFenceCenterSelected ?? (() => {})}
				onStopPickingFenceCenter={onStopPickingFenceCenter ?? (() => {})}
			/>

			{resolvedGeofences.map((g) => (
				<GeofenceNotificationController
					key={g.id}
					geofence={g}
					locations={locations}
				/>
			))}

			<IconButton
				onClick={onToggleGeofencePanel}
				sx={{
					position: "absolute",
					top: 12,
					right: 12,
					zIndex: 1200,
					backgroundColor: "rgba(15, 23, 42, 0.92)",
					color: "white",
					boxShadow: 3,
					borderRadius: "50%",
					width: 48,
					height: 48,
					"&:hover": {
						backgroundColor: "rgba(30, 41, 59, 0.96)",
					},
				}}
				title={
					geofencePanelOpen ? t("map.closeVirtualFence") : t("map.openVirtualFence")
				}
			>
				{geofencePanelOpen ? (
					<CloseFullscreenIcon />
				) : (
					<SettingsInputAntennaIcon />
				)}
			</IconButton>

			{markersToRender.map((loc) => {
				const plateLabel = formatPlateDisplay(loc.vehiclePlate || loc.entityId);
				const addressLines = splitAddressAfterCity(loc.address);

				return (
					<Fragment key={loc.entityId}>
						<DriftMarker
							key={`vehicle-${loc.entityId}`}
							position={[loc.latitude, loc.longitude]}
							duration={1000}
							icon={defaultIcon}
							eventHandlers={{
								click: () => onSelect(loc.entityId),
							}}
						>
							<Popup
								autoClose={false}
								closeButton={true}
								autoPan={false}
								keepInView={false}
							>
								<div
									style={{
										fontFamily: "Inter, Roboto, sans-serif",
										fontSize: "11px",
										lineHeight: 1.3,
										color: "#333",
										padding: "2px 4px",
									}}
								>
									<div style={{ fontWeight: 600 }}>{plateLabel}</div>
									{!!loc.modelName && (
										<div style={{ color: "#777" }}>{loc.modelName}</div>
									)}
									<div style={{ color: "#777" }}>
										{addressLines.firstLine}
										{addressLines.secondLine && (
											<>
												<br />
												{addressLines.secondLine}
											</>
										)}
									</div>
									<div style={{ color: "#777" }}>
										Lat/Lng: {loc.latitude.toFixed(6)},{" "}
										{loc.longitude.toFixed(6)}
									</div>
								</div>
							</Popup>
						</DriftMarker>

						<Marker
							key={`plate-${loc.entityId}`}
							position={[loc.latitude, loc.longitude]}
							icon={createPlateIcon(plateLabel)}
							interactive={false}
							keyboard={false}
							zIndexOffset={-1000}
						/>
					</Fragment>
				);
			})}

			<InitialBoundsController locations={markersToRender} />

			<MapController
				selectedLocation={selectedLocation}
				isFollowing={isFollowing}
			/>
			{focusedGeofence?.center && (
				<FocusedGeofenceController geofence={focusedGeofence} />
			)}
			<ResizeController trigger={geofencePanelOpen} />

			{selectedId && isFollowing && (
				<MuiTooltip title={t("map.stopFollow")}>
					<IconButton
						onClick={() => {
							setIsFollowing(false);
							onStopFollow?.();
						}}
						sx={{
							position: "absolute",
							top: 90,
							left: 5,
							zIndex: 1000,
							backgroundColor: "rgba(15, 23, 42, 0.92)",
							color: "white",
							boxShadow: 3,
							borderRadius: "50%",
							width: 48,
							height: 48,
							"&:hover": { backgroundColor: "rgba(30, 41, 59, 0.96)", opacity: 1 },
						}}
					>
						<StopIcon />
					</IconButton>
				</MuiTooltip>
			)}
		</MapContainer>
	);
}
